import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { BehaviorSubject } from 'rxjs';
import { ISelectOption } from 'src/app/models/selectOptions/selectOption.interface';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { RoleApiResponse } from 'src/app/models/api/accountManagement.model';
import { getRoleDescriptionById } from 'src/app/models/constants/accessGroupRoles.constants';

function arrayNotEmpty(): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    if (!Array.isArray(value) || value.length === 0) {
      return { required: true };
    }
    return null;
  };
}

function uniqueName(existingNames: string[], currentName?: string): ValidatorFn {
  return (control: AbstractControl) => {
    const value = (control.value || '').trim().toLowerCase();
    if (!value) {
      return null;
    }
    const isDuplicate = existingNames.some(
      (name) => name.toLowerCase() === value && name.toLowerCase() !== (currentName || '').toLowerCase()
    );
    return isDuplicate ? { uniqueName: true } : null;
  };
}

export interface AccessGroupDialogData {
  mode: 'create' | 'edit';
  existingNames: string[];
  accessGroup?: {
    id: string;
    name: string;
    roleId: string;
    users: string[];
    integrationIds: string[];
    allIntegrations: boolean;
  };
}

export interface AccessGroupDialogResult {
  name: string;
  integrations: string[];
  allIntegrations: boolean;
  role: string | null;
  users: string[];
}

@Component({
  selector: 'app-access-group-dialog',
  templateUrl: './access-group-dialog.component.html',
  styleUrls: ['./access-group-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessGroupDialogComponent implements OnInit {
  form: FormGroup;

  integrationOptions$ = new BehaviorSubject<ISelectOption[]>([]);
  userOptions$ = new BehaviorSubject<ISelectOption[]>([]);

  selectedIntegrationIds: string[] = [];
  selectedUserEmails: string[] = [];

  integrationValid$ = new BehaviorSubject<boolean>(true);

  isEditMode = false;
  dialogTitle = 'Create new access group';
  submitButtonText = 'Create';

  roles: RoleApiResponse[] = [];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _dialogRef: DialogRef<AccessGroupDialogResult>,
    private readonly _storeService: StoreService,
    private readonly _destroyRef: DestroyRef,
    private readonly _accountHttpService: AccountManagementHttpService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone,
    @Optional() @Inject(DIALOG_DATA) public data: AccessGroupDialogData | null
  ) {
    this.isEditMode = data?.mode === 'edit';
    this.dialogTitle = this.isEditMode ? 'Edit access group' : 'Create new access group';
    this.submitButtonText = this.isEditMode ? 'Save changes' : 'Create';

    const existingNames = data?.existingNames || [];
    const currentName = this.isEditMode ? data?.accessGroup?.name : undefined;

    this.form = this._fb.group({
      name: ['', [Validators.required, Validators.maxLength(200), uniqueName(existingNames, currentName)]],
      scopeCategory: [null, Validators.required],
      allIntegrations: [false],
      integrations: [[], arrayNotEmpty()],
      role: [null, Validators.required],
      users: [[]],
    });

    if (this.isEditMode && data?.accessGroup) {
      this._initEditMode(data.accessGroup);
    }
  }

  private _initEditMode(accessGroup: NonNullable<AccessGroupDialogData['accessGroup']>): void {
    this.selectedIntegrationIds = accessGroup.integrationIds;

    this.form.patchValue({
      name: accessGroup.name,
      scopeCategory: 'integration',
      allIntegrations: accessGroup.allIntegrations,
      integrations: accessGroup.integrationIds,
      role: accessGroup.roleId,
    });

    this._updateIntegrationsValidation(accessGroup.allIntegrations);
  }

  onAllIntegrationsToggle(checked: boolean): void {
    this._updateIntegrationsValidation(checked);
  }

  private _updateIntegrationsValidation(allIntegrations: boolean): void {
    const integrationsControl = this.form.get('integrations');
    const scopeCategoryControl = this.form.get('scopeCategory');

    if (allIntegrations) {
      integrationsControl?.clearValidators();
      scopeCategoryControl?.clearValidators();
      this.integrationValid$.next(true);
    } else {
      integrationsControl?.setValidators(arrayNotEmpty());
      scopeCategoryControl?.setValidators(Validators.required);
      this.integrationValid$.next(this.selectedIntegrationIds.length > 0);
    }
    integrationsControl?.updateValueAndValidity();
    scopeCategoryControl?.updateValueAndValidity();
  }

  ngOnInit(): void {
    this._loadIntegrations();
    this._loadUsers();
    this._loadRoles();
  }

  roleTitle(value: string | null): string {
    if (!value) return '';
    const role = this.roles.find((r) => r.id === value);
    return role?.name ?? value;
  }

  onIntegrationSelected(selected: ISelectOption[]): void {
    this.selectedIntegrationIds = selected.map((o) => o.id);
    this.form.get('integrations')?.setValue(this.selectedIntegrationIds);
    const isValid = this.selectedIntegrationIds.length > 0;
    this.integrationValid$.next(isValid);
    this.form.get('integrations')?.markAsTouched();
    this.form.get('integrations')?.updateValueAndValidity();
  }

  onUserSelected(selected: ISelectOption[]): void {
    this.selectedUserEmails = selected.map((o) => o.id);
    this.form.get('users')?.setValue(this.selectedUserEmails);
    this.form.get('users')?.markAsTouched();
    this.form.get('users')?.updateValueAndValidity();
  }

  private _loadIntegrations(): void {
    this._storeService.integrations$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((integrations) => {
      const options: ISelectOption[] = integrations.map((integration) => ({
        id: integration.id,
        value: integration.id,
        displayName: integration.name,
      }));

      if (this.isEditMode && this.data?.accessGroup?.integrationIds) {
        this.selectedIntegrationIds = [...this.data.accessGroup.integrationIds];
        this._cdr.detectChanges();
        this._ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this._ngZone.run(() => {
              this.integrationOptions$.next(options);
              this._cdr.detectChanges();
            });
          });
        });
      } else {
        this.integrationOptions$.next(options);
      }
    });
  }

  private _loadUsers(): void {
    this._accountHttpService
      .getUsersWithExpandOption('none')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((users) => {
        const options: ISelectOption[] = users.map((user) => ({
          id: user.email,
          value: user.email,
          displayName: user.email,
        }));

        if (this.isEditMode && this.data?.accessGroup?.users) {
          // The API may return either user IDs or emails in the users array
          // Build maps for both ID->email and email->email lookups
          const userIdToEmail = new Map(users.map((u) => [u.userId, u.email]));
          const validEmails = new Set(users.map((u) => u.email));

          const emails = this.data.accessGroup.users
            .map((userIdOrEmail) => {
              // First check if it's already a valid email
              if (validEmails.has(userIdOrEmail)) {
                return userIdOrEmail;
              }
              // Otherwise try to convert from user ID
              return userIdToEmail.get(userIdOrEmail);
            })
            .filter((email): email is string => !!email);

          this.selectedUserEmails = emails;
          this.form.get('users')?.setValue(emails);
          this._cdr.detectChanges();
          this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
              this._ngZone.run(() => {
                this.userOptions$.next(options);
                this._cdr.detectChanges();
              });
            });
          });
        } else {
          this.userOptions$.next(options);
        }
      });
  }

  private _loadRoles(): void {
    this._accountHttpService
      .getRoles()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((roles) => {
        // Merge backend roles with local descriptions as fallback
        this.roles = roles.map((role) => ({
          ...role,
          description: role.description || getRoleDescriptionById(role.id),
        }));
        this._cdr.detectChanges();
      });
  }

  cancel(): void {
    this._dialogRef.close();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const allIntegrations = this.form.get('allIntegrations')?.value;
      if (!allIntegrations) {
        this.integrationValid$.next(this.selectedIntegrationIds.length > 0);
      }
      return;
    }
    const value = this.form.value as {
      name: string;
      scopeCategory: string;
      allIntegrations: boolean;
      integrations: string[];
      role: string | null;
      users: string[];
    };
    const result: AccessGroupDialogResult = {
      name: value.name,
      integrations: value.allIntegrations ? [] : value.integrations,
      allIntegrations: value.allIntegrations,
      role: value.role,
      users: value.users,
    };
    this._dialogRef.close(result);
  }
}
